<script setup lang="ts">
import { ref, watch } from 'vue';

import { adminApi, type WithdrawalRow } from './admin-api';
import { money, usePane, when } from './pane';

const page = ref(1);
const statusFilter = ref('');
const pane = usePane(() => adminApi.withdrawals({ page: page.value, ...(statusFilter.value ? { status: statusFilter.value } : {}) }));
watch([page, statusFilter], () => void pane.reload());

const rejectTarget = ref<WithdrawalRow | null>(null);
const rejectReason = ref('');

function statusClass(s: string): string {
  if (s === 'APPROVED') return 'ok';
  if (s === 'REJECTED' || s === 'FAILED') return 'bad';
  return 'warn';
}

function approve(w: WithdrawalRow): void {
  if (!confirm(`Approve withdrawal ${w.id} for ${money(w.amountCents, w.currency)}?`)) return;
  void pane.act(() => adminApi.withdrawalApprove(w.id));
}

function openReject(w: WithdrawalRow): void {
  rejectTarget.value = w;
  rejectReason.value = '';
}

function confirmReject(): void {
  if (!rejectTarget.value || !rejectReason.value.trim()) return;
  const id = rejectTarget.value.id;
  rejectTarget.value = null;
  void pane.act(() => adminApi.withdrawalReject(id, rejectReason.value.trim()));
}

function checkStatus(w: WithdrawalRow): void {
  void pane.act(() => adminApi.withdrawalCheckStatus(w.id));
}

const pages = () => {
  const d = pane.data.value;
  return d ? Math.max(1, Math.ceil(d.total / d.pageSize)) : 1;
};
</script>

<template>
  <h1>Withdrawals</h1>

  <div v-if="pane.data.value" class="summary">
    <span>{{ pane.data.value.total }} total</span>
    <span v-if="pane.data.value.pendingTotalCents > 0" class="pending-sum">
      {{ money(pane.data.value.pendingTotalCents, 'EUR') }} pending
    </span>
  </div>

  <div class="filters">
    <select v-model="statusFilter" data-test="withdrawal-status-filter">
      <option value="">All statuses</option>
      <option value="PENDING">Pending</option>
      <option value="PROCESSING">Processing</option>
      <option value="APPROVED">Approved</option>
      <option value="REJECTED">Rejected</option>
      <option value="FAILED">Failed</option>
    </select>
  </div>

  <p v-if="pane.loading.value && !pane.data.value">Loading…</p>
  <p v-if="pane.error.value" class="bad">{{ pane.error.value }}</p>

  <div v-if="pane.data.value" class="table-wrap">
    <table>
      <thead>
        <tr>
          <th>Date</th>
          <th>User</th>
          <th>Method</th>
          <th class="num">Gross</th>
          <th class="num">Fee</th>
          <th class="num">Net</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="pane.data.value.items.length === 0">
          <td colspan="8" class="empty">No withdrawals</td>
        </tr>
        <tr v-for="w in pane.data.value.items" :key="w.id" :data-withdrawal="w.id">
          <td class="when">{{ when(w.createdAt) }}</td>
          <td>{{ w.user?.email ?? '—' }}</td>
          <td>{{ w.payoutMethod ?? '—' }}</td>
          <td class="num">{{ money(w.amountCents, w.currency) }}</td>
          <td class="num">{{ money(w.feeCents, w.currency) }}</td>
          <td class="num">{{ money(w.netCents, w.currency) }}</td>
          <td>
            <span class="chip" :class="statusClass(w.status)">{{ w.status }}</span>
            <span v-if="w.reason" class="reason">{{ w.reason }}</span>
          </td>
          <td class="actions">
            <button
              v-if="w.status === 'PENDING'"
              class="act ok"
              :disabled="pane.loading.value"
              @click="approve(w)"
            >Approve</button>
            <button
              v-if="w.status === 'PENDING'"
              class="act bad"
              :disabled="pane.loading.value"
              @click="openReject(w)"
            >Reject</button>
            <button
              v-if="w.status === 'PROCESSING'"
              class="act"
              :disabled="pane.loading.value"
              @click="checkStatus(w)"
            >Check status</button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <div v-if="pages() > 1" class="pager">
    <button :disabled="page <= 1" @click="page--">&laquo; Prev</button>
    <span>{{ page }} / {{ pages() }}</span>
    <button :disabled="page >= pages()" @click="page++">Next &raquo;</button>
  </div>

  <!-- Reject modal -->
  <div v-if="rejectTarget" class="modal-backdrop" @click.self="rejectTarget = null">
    <div class="modal" data-test="reject-modal">
      <h3>Reject withdrawal</h3>
      <p>{{ money(rejectTarget.amountCents, rejectTarget.currency) }} — {{ rejectTarget.user?.email ?? rejectTarget.id }}</p>
      <label>
        Reason
        <textarea v-model="rejectReason" rows="3" placeholder="Why is this being rejected?" />
      </label>
      <div class="modal-actions">
        <button class="act bad" :disabled="!rejectReason.trim()" @click="confirmReject">Reject</button>
        <button class="act" @click="rejectTarget = null">Cancel</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.summary {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.5rem;
  color: var(--steel-300);
  font-size: 0.8rem;
}

.pending-sum {
  color: var(--accent, #e8722c);
  font-weight: 600;
}

.filters {
  margin-bottom: 0.75rem;
}

.filters select {
  padding: 0.3rem 0.5rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.2rem;
  background: var(--steel-800);
  color: var(--steel-200);
  font: inherit;
  font-size: 0.78rem;
}

.table-wrap {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
}

th {
  padding: 0.35rem 0.5rem;
  border-bottom: 1px solid var(--steel-600);
  color: var(--steel-400);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-align: left;
  text-transform: uppercase;
}

td {
  padding: 0.35rem 0.5rem;
  border-bottom: 1px solid var(--steel-700);
  color: var(--steel-200);
}

.num { text-align: right; font-variant-numeric: tabular-nums; }
.when { white-space: nowrap; }
.empty { text-align: center; color: var(--steel-400); padding: 1.5rem; }

.reason {
  display: block;
  margin-top: 0.15rem;
  color: var(--steel-400);
  font-size: 0.7rem;
}

.actions {
  display: flex;
  gap: 0.3rem;
  white-space: nowrap;
}

.act {
  padding: 0.2rem 0.5rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.15rem;
  background: var(--steel-800);
  color: var(--steel-200);
  font: inherit;
  font-size: 0.7rem;
  cursor: pointer;
}

.act.ok {
  border-color: #3a7;
  color: #3a7;
}

.act.bad {
  border-color: #e55;
  color: #e55;
}

.act:disabled {
  opacity: 0.5;
  cursor: default;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
  color: var(--steel-300);
  font-size: 0.78rem;
}

.pager button {
  padding: 0.25rem 0.6rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.15rem;
  background: var(--steel-800);
  color: var(--steel-200);
  font: inherit;
  font-size: 0.75rem;
  cursor: pointer;
}

.pager button:disabled {
  opacity: 0.4;
  cursor: default;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  z-index: 100;
}

.modal {
  width: 24rem;
  max-width: 90vw;
  padding: 1.2rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.3rem;
  background: var(--steel-800);
}

.modal h3 {
  margin: 0 0 0.5rem;
  color: var(--steel-200);
}

.modal p {
  margin: 0 0 0.75rem;
  color: var(--steel-300);
  font-size: 0.8rem;
}

.modal label {
  display: block;
  color: var(--steel-400);
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.modal textarea {
  display: block;
  width: 100%;
  margin-top: 0.3rem;
  padding: 0.4rem;
  border: 1px solid var(--steel-600);
  border-radius: 0.15rem;
  background: var(--steel-900);
  color: var(--steel-200);
  font: inherit;
  font-size: 0.8rem;
  resize: vertical;
}

.modal-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
}
</style>
